import { UUID } from '@cmg/common';
import { Grid, Stack, Typography } from '@cmg/design-system';
import { useIntl } from '@cmg/i18n';
import React, { Fragment } from 'react';

import { getFeatureToggles } from '../../../../../../../../common/config';
import { isIndicationStatusAndTypeValid } from '../../../../../../../order-book/utils';
import { useIdentifyOrderBookPersona } from '../../../../../hooks/useIdentifyOrderBookPersona';
import GridRowPair from '../../../manage-indication/common/indication-section-read-view/grid-row-pair/GridRowPair';
import { AllocationDetailsDataType } from '../../Allocation.model';
import { AllocationAcknowledgementStatusLabel } from '../allocation-acknowledgement-status/AllocationAcknowledgementStatus.model';
import { getMessage } from './AllocationDetails.model';

export type Props = Readonly<{
  offeringId: UUID;
  allocationData: AllocationDetailsDataType;
  isReleased: boolean;
}>;

/**
 * Header for final Allocation
 */
export const AllocationDetails: React.FC<Props> = ({ allocationData, offeringId, isReleased }) => {
  const intl = useIntl();
  const { isBuySideEconomicsOn } = getFeatureToggles();
  const { type: personaType, isSellSide } = useIdentifyOrderBookPersona({ offeringId });
  const {
    indicationStatus,
    orderType,
    shareQuantity,
    offerPrice,
    pricingCurrencyCode,
    isOrderBookOpen,
    investorReplyStatus,
    sellingConcession,
    tradeDate,
    settlementDate,
  } = allocationData;

  const allocationConfirmation =
    isReleased && investorReplyStatus
      ? AllocationAcknowledgementStatusLabel[investorReplyStatus]
      : '-';

  const label = getMessage({ personaType, indicationStatus, orderType, isOrderBookOpen });

  const isValid =
    indicationStatus && orderType && isIndicationStatusAndTypeValid(indicationStatus, orderType);
  const showLabel = !isValid && label;

  const allocationShares = isReleased
    ? intl.formatNumber(shareQuantity, { format: 'integer' })
    : '-';
  const offerPriceValue = isReleased
    ? intl.formatNumber(offerPrice || null, { format: 'currency', currency: pricingCurrencyCode })
    : '-';

  const allocationNotionalValue = shareQuantity ? shareQuantity * offerPrice : null;
  const allocationNotional = isReleased
    ? intl.formatNumber(allocationNotionalValue, {
        format: 'currency',
        currency: pricingCurrencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
    : '-';

  const allocationConfirmationValue = isReleased ? allocationConfirmation : '-';

  return (
    <Stack gap={1} aria-label="Allocation details">
      {showLabel && (
        <Typography variant="body1">
          {label.prefix}
          <Typography variant="highlight1">{label.state}</Typography>
          {label.suffix}
        </Typography>
      )}
      {!showLabel && (
        <Grid container rowGap={1} data-testid="read-allocation-details">
          <GridRowPair label="Allocation Shares" value={allocationShares} />
          <GridRowPair label="Offer Price" value={offerPriceValue} />
          <GridRowPair label="Allocation Notional" value={allocationNotional} />
          {isSellSide && (
            <GridRowPair label="Allocation Confirmation" value={allocationConfirmationValue} />
          )}
          {!isSellSide && isBuySideEconomicsOn && (
            <Fragment>
              <GridRowPair
                label="Selling Concession"
                value={intl.formatNumber(sellingConcession, {
                  format: 'currencyMedium',
                  currency: pricingCurrencyCode,
                })}
              />
              <GridRowPair
                label="Trade Date"
                value={intl.formatDate(tradeDate, { timeZone: 'UTC' })}
              />
              <GridRowPair
                label="Settlement Date"
                value={intl.formatDate(settlementDate, { timeZone: 'UTC' })}
              />
            </Fragment>
          )}
        </Grid>
      )}
    </Stack>
  );
};

export default AllocationDetails;
